/**
 * BuildBadge — Version/build visibility for Edit and Presenter modes only.
 *
 * NEVER render this in Display mode (student-facing).
 *
 * Shows:
 *   - Environment label (Production / Preview / Dev)
 *   - Frontend build SHA (short, from VITE_BUILD_SHA env var)
 *   - Backend revision + short git SHA (fetched from /health on mount)
 *   - Build date (from VITE_BUILD_DATE env var, or 'unknown')
 *   - Copy Debug Info button
 *
 * Placement: bottom-left corner of the board, small, low-opacity until hover.
 * Designed to help testers and bug reporters — not to be noticeable in normal use.
 */
import React, { useState, useEffect } from 'react';
import { Copy, Check } from 'lucide-react';
import api from '../../services/api';

// Resolve environment label from build-time global (injected by vite.config.js define)
// Falls back to DEV detection if running outside of a Vite build.
/* eslint-disable no-undef */
const ENV_LABEL = (typeof __APP_ENV__ !== 'undefined' ? __APP_ENV__ : null)
    || (typeof import.meta !== 'undefined' && import.meta.env?.DEV ? 'Dev' : 'Production');
const FRONTEND_SHA = (typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : 'local')?.slice(0, 7);
const BUILD_DATE = (typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : null);
/* eslint-enable no-undef */

const BuildBadge = () => {
    const [backendInfo, setBackendInfo] = useState(null);
    const [copied, setCopied] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        // Non-blocking — if it fails, the badge still shows frontend info
        api.get('/health')
            .then(res => {
                const v = res.data?.version || {};
                setBackendInfo({
                    sha: v.git_sha?.slice(0, 7) || '?',
                    revision: v.deployment_id || '?',
                });
            })
            .catch(() => {
                setBackendInfo({ sha: '?', revision: '?' });
            });
    }, []);

    const buildDebugText = () => {
        const lines = [
            `Env: ${ENV_LABEL}`,
            `Frontend SHA: ${FRONTEND_SHA}`,
            `Backend SHA: ${backendInfo?.sha ?? 'loading...'}`,
            `Backend revision: ${backendInfo?.revision ?? 'loading...'}`,
        ];
        if (BUILD_DATE) lines.push(`Build date: ${BUILD_DATE}`);
        lines.push(`Timestamp: ${new Date().toISOString()}`);
        return lines.join('\n');
    };

    const handleCopy = async (e) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(buildDebugText());
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Fallback for older browsers / clipboard denied
            const ta = document.createElement('textarea');
            ta.value = buildDebugText();
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const envColor = ENV_LABEL === 'Production'
        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
        : ENV_LABEL === 'Preview'
            ? 'bg-amber-500/20 text-amber-300 border-amber-500/30'
            : 'bg-purple-500/20 text-purple-300 border-purple-500/30';

    return (
        <div
            className="flex items-center gap-1.5 group cursor-pointer select-none"
            style={{ fontSize: 10 }}
            onClick={() => setExpanded(v => !v)}
            title="Click to expand build info"
        >
            {/* Env pill — always visible */}
            <span className={`px-1.5 py-0.5 rounded border font-medium ${envColor}`}
                style={{ fontSize: 9, letterSpacing: '0.03em' }}>
                {ENV_LABEL}
            </span>

            {/* Short SHA — always visible */}
            <span className="text-white/30 group-hover:text-white/60 transition-colors font-mono">
                {FRONTEND_SHA}
            </span>

            {/* Expanded detail */}
            {expanded && backendInfo && (
                <span className="text-white/40 font-mono">
                    · be:{backendInfo.sha}
                </span>
            )}
            {expanded && BUILD_DATE && (
                <span className="text-white/30 font-mono">
                    · {BUILD_DATE}
                </span>
            )}

            {/* Copy button */}
            <button
                onClick={handleCopy}
                className="p-0.5 rounded text-white/30 hover:text-white/70 hover:bg-white/10 transition-all opacity-0 group-hover:opacity-100"
                title="Copy debug info"
            >
                {copied
                    ? <Check size={10} className="text-emerald-400" />
                    : <Copy size={10} />
                }
            </button>
        </div>
    );
};

export default BuildBadge;
